import * as React from 'react';

import { ROSTER_NOT_FOUND_ERROR } from '@showbie-socrative/socrative-utils';
import { useDispatch, useSelector } from 'react-redux';

import { GenerateReportEmailStudentsEventAdapter } from '~analytics/index';
import { useReportAnalytics } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';
import { ReportRecipientsScreen } from '~teacher/enums';
import { Student } from '~teacher/models/reports';
import { selectStudents } from '~teacher/slices/emailReports/emailReportsSelectors';
import {
  populateStudentRoster,
  toggleAllStudents,
  untoggleAllStudents,
} from '~teacher/slices/emailReports/emailReportsSlice';
import {
  emailReportsToStudents,
  fetchStudentRoster,
} from '~teacher/slices/emailReports/emailReportsThunks';
import { selectIsRequestInProgress } from '~teacher/slices/teacher/teacherSelector';
import { requestFinished } from '~teacher/slices/teacher/teacherSlice';
import { proCheck } from '~teacher/util/proCheck';

import {
  Props as DialogProps,
  EmailStudentsDialog,
} from './EmailStudentsDialog';

interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  activityInstanceId: number;
}

export interface SendEmailOptions {
  includeExplanations: boolean;
  includeCorrectAnswers: boolean;
}

export function ShareResults({
  open,
  onClose,
  activityInstanceId,
}: React.PropsWithChildren<Props>) {
  const dispatch = useDispatch();
  const canShareResults = proCheck.canShareResults();
  const reportAnalytics = useReportAnalytics();
  const [currentModalView, setCurrentModalView] = React.useState(
    ReportRecipientsScreen.REPORT_OPTIONS_VIEW
  );
  const handleSelectAll = () => {
    dispatch(toggleAllStudents());
  };
  const handleSelectNone = () => {
    dispatch(untoggleAllStudents());
  };

  React.useEffect(() => {
    // Only do the fetch if the dialog is open.
    if (canShareResults && open) {
      const fetchStudents = async () => {
        try {
          setCurrentModalView(ReportRecipientsScreen.REPORT_OPTIONS_VIEW);
          await dispatch(fetchStudentRoster(activityInstanceId));
        } catch (error) {
          console.error(error);
          const { response } = error;
          if (response && response?.data) {
            const { code } = response.data;
            if (code === ROSTER_NOT_FOUND_ERROR.code) {
              dispatch(populateStudentRoster({ studentRoster: [] }));
              dispatch(requestFinished());
              return;
            }
          }
          setCurrentModalView(ReportRecipientsScreen.ROSTER_FETCH_ERROR);
          dispatch(requestFinished());
        }
      };

      fetchStudents();
    }
  }, [canShareResults, open, dispatch, activityInstanceId]);

  const isRequestInProgress = useSelector(selectIsRequestInProgress);
  const students = useSelector(selectStudents);

  const sendEmailsToStudents = async (payload: {
    activity_instance_id: number;
    student_ids: string[];
    options: {
      include_correct_answers: boolean;
      include_explanations: boolean;
    };
  }) => {
    try {
      await dispatch(emailReportsToStudents(payload));
      setCurrentModalView(ReportRecipientsScreen.EMAIL_SUCCESS_VIEW);
      new GenerateReportEmailStudentsEventAdapter({
        include_correct_answers: payload.options.include_correct_answers,
        include_explanations: payload.options.include_explanations,
        recipient_count: payload.student_ids.length,
        analytics: reportAnalytics,
      }).sendAnalytics();
    } catch (error) {
      console.error(error);
      dispatch(requestFinished());
      setCurrentModalView(ReportRecipientsScreen.ERROR_FEEDBACK_VIEW);
    }
  };

  const handleEmailAllStudents = async (reportOptions: SendEmailOptions) => {
    const studentIds = students.map((student: Student) => {
      return String(student.studentId);
    });
    const { includeCorrectAnswers, includeExplanations } = reportOptions;
    await sendEmailsToStudents({
      activity_instance_id: activityInstanceId,
      student_ids: studentIds,
      options: {
        include_correct_answers: includeCorrectAnswers ?? false,
        include_explanations: includeExplanations ?? false,
      },
    });
  };

  const handleEmailIndividuals = async (reportOptions: SendEmailOptions) => {
    const studentIds = students
      .filter((student: Student) => {
        return student.isSelected;
      })
      .map((student: Student) => {
        return String(student.studentId);
      });
    const { includeCorrectAnswers, includeExplanations } = reportOptions;
    await sendEmailsToStudents({
      activity_instance_id: activityInstanceId,
      student_ids: studentIds,
      options: {
        include_correct_answers: includeCorrectAnswers ?? false,
        include_explanations: includeExplanations ?? false,
      },
    });
  };

  return (
    <EmailStudentsDialog
      open={open}
      onClose={onClose}
      isRequestInProgress={isRequestInProgress}
      onEmailIndividuals={handleEmailIndividuals}
      onEmailAllStudents={handleEmailAllStudents}
      students={students}
      currentModalView={currentModalView}
      setCurrentModalView={setCurrentModalView}
      onSelectAll={handleSelectAll}
      onSelectNone={handleSelectNone}
    />
  );
}
